/**
 * =============================================================================
 *  BUILD_ID —— `persistQueryClient` 的 `buster`（ARCHITECTURE §10.2 規則 1）。
 *
 *  規格逐字：「`maxAge` 24h，`buster` 綁 build hash」。綁對了，一次部署就會讓
 *  所有人硬碟上的舊快取作廢；綁錯了，使用者明天早上看到的是**昨天那一版前端
 *  解讀出來的今天資料**——而 payload 形狀改過的話，那是一個不會丟例外、
 *  只會少畫一條線的錯誤。
 *
 *  hash 從哪裡來：vite.config.ts 的 `entryFileNames: 'assets/[name]-[hash].js'`
 *  已經把內容雜湊寫進了模組的 URL，所以 `import.meta.url` 本身就是 build hash
 *  的載體。這條路徑的好處是**不需要在建置設定裡多塞一個 define**——多一個
 *  define 就多一個「本機改了、CI 沒改」的失效模式，而且 `Date.now()` 那種寫法
 *  會讓每一次建置都不可重現。
 *
 *  ── 為什麼「只綁一個 chunk」不是漏洞（這一段被誤讀過一次）──────────────
 *
 *  本檔被打包進 entry chunk，而 dashboard / PriceChart / StatRangeCard /
 *  formatters 都在各自的 lazy chunk 裡。於是很自然會得到一個結論：
 *  「改動只落在那些檔案時 entry chunk 沒變，buster 不變，快取不會作廢。」
 *
 *  **實測不成立。** Vite 的 entry chunk 裡寫著**每一個** lazy chunk 的「已含內容
 *  雜湊的檔名」（`__vitePreload` 與動態 import 改寫的產物），所以雜湊是遞移的：
 *
 *      改 StatRangeCard.tsx → StatRangeCard-*.js 的雜湊變
 *                           → entry chunk 裡那個字串跟著變
 *                           → entry chunk 自己的雜湊變 → BUILD_ID 變。
 *
 *  實測數字：只把 StatRangeCard 的一個 class 從 `blur-[6px]` 改成 `blur-[7px]`，
 *  `index-Dl2F9x2N.js` → `index-Cdl5OzO-.js`。巢狀的 lazy chunk 同理（A 動態
 *  import B，B 的雜湊寫在 A 裡，A 的雜湊寫在 entry 裡）。
 *
 *  **但這是一個沒有人宣告過的相依性**，而且它只在 Vite 目前的 chunk 載入策略下成立。
 *  所以 `tools/verify-cache-buster.mjs` 在每次 `npm run verify` 的 build 之後對
 *  `dist/` 直接斷言它：帶著 CACHE_SCHEMA_VERSION 的那個 chunk 必須提到其餘每一個
 *  chunk 的含雜湊檔名。斷言失敗就是 build 失敗，不是一句註解。
 *
 *  dev（vite serve）沒有雜湊，回 `dev`：開發時本來就該每次都重抓。
 * ========================================================================== */

/**
 * 快取 schema 版本。**手動遞增。**
 *
 * build hash 管的是「前端程式碼變了」（含所有 lazy chunk，見上面那段遞移性）。
 * 這個常數管的是另一件 build hash 管不到的事：**後端回傳的 payload 形狀變了，
 * 而前端一行都沒改**——例如 `/ohlcv` 少了一個 indicator key、或 KPI item 多一個
 * 欄位。那種情況下 dist 逐位元相同，BUILD_ID 也就相同，但硬碟上那份快取已經
 * 對不上新的讀取程式了。**改到 API 回傳形狀時，手動遞增這個常數。**
 */
export const CACHE_SCHEMA_VERSION = 'p2.1';

/** `/assets/index-T1UzLX0a.js` → `T1UzLX0a`；沒有雜湊的路徑 → `dev`。 */
export function extractBuildHash(moduleUrl: string): string {
  const file = moduleUrl.split('?')[0].split('#')[0].split('/').pop() ?? '';
  // Vite 的預設雜湊是 base64url 風格的 8 碼；放寬到 8–24 以容忍設定調整。
  const match = /-([A-Za-z0-9_-]{8,24})\.[cm]?js$/.exec(file);
  return match === null ? 'dev' : match[1];
}

export const BUILD_ID = `${CACHE_SCHEMA_VERSION}-${extractBuildHash(import.meta.url)}`;
